/**
 * 主题 Context：深色/浅色整站切换（CLAUDE.md §前端规范 §状态管理 → 跨组件共享用 React Context）。
 *
 * 设计：
 *   - 持久化在 localStorage["ad_tech_theme"]，取值 "light" | "dark"，默认 "light"。
 *   - 通过给 <html> 增/删 `dark` 类切换主题（tailwind.config darkMode:"class" +
 *     index.css 的 :root / .dark 变量集）。
 *   - 首屏防闪烁：index.html 内联脚本在 React 挂载前已据 localStorage 设置好 `dark` 类，
 *     本 Provider 挂载时同步一次状态与之对齐。
 * 适用章节：LEO v1.0 §2.1 设计 Token（:root 变量体系）。
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Theme = "light" | "dark";

interface ThemeContextValue {
  theme: Theme;
  /** 切换为指定主题（同步 <html> 类 + localStorage）。 */
  setTheme: (next: Theme) => void;
  /** 在深/浅之间翻转。 */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const THEME_KEY = "ad_tech_theme";

/** 读 localStorage 的主题；非法/缺省回退 light。 */
function readStoredTheme(): Theme {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

/** 将主题同步到 <html> 的 dark 类与 color-scheme（index.html 内联脚本已做首屏，这里保证后续一致）。 */
function syncHtmlClass(theme: Theme) {
  const root = document.documentElement;
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());

  // 挂载时按当前状态同步一次 <html> 类（与内联脚本对齐，防止状态/类不一致）。
  useEffect(() => {
    syncHtmlClass(theme);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      /* localStorage 不可用时仅会话内生效，忽略 */
    }
    syncHtmlClass(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next: Theme = prev === "dark" ? "light" : "dark";
      try {
        localStorage.setItem(THEME_KEY, next);
      } catch {
        /* ignore */
      }
      syncHtmlClass(next);
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within <ThemeProvider>");
  }
  return ctx;
}
